/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Injectable } from '@angular/core'
import * as _ from 'lodash'
import { Role } from '../../models/clikcrm/RBAC'
import { PermissionService } from './permission.service'
import { UserSessionService } from '../user-session.service'
import { HttpClient } from '@angular/common/http'
import * as uuid from 'uuid'
import { CanActivate } from '@angular/router'
import { ClikCRMService } from './clikCRM.service'

@Injectable({
    providedIn: 'root',
})
export class AuthService implements CanActivate {
    constructor(
        private $http: HttpClient,
        private permissionService: PermissionService,
        private Session: UserSessionService,
        private clikCRMService: ClikCRMService
    ) {}

    public login_strategy: string = ''
    private logoutPromise: Promise<any> | null = null

    getUserRoles(): Array<Role> {
        console.debug('Getting Roles for ' + JSON.stringify(this.Session))
        if (sessionStorage.getItem('tdw.auth.authed') && this.Session.userRoles) {
            // return a copy.
            return this.Session.userRoles.slice()
        }
        return []
    }

    isLoginStrategySet(): boolean {
        return null !== this.login_strategy
    }
    useSAMLAuthentication(): boolean {
        console.debug('UseSAMLAuthentication: ' + this.login_strategy)
        // TODO: Retrieve this value from the server.
        return 'saml' === this.login_strategy
    }

    authenticated() {
        return 'true' === sessionStorage.getItem('tdw.auth.authed')
    }

    // Check to see if the given application feature is authorized to this user.
    // Caution, this is a client side check only currently.
    // The server will also check on that side if an action is invoked.
    authorized(checkPermission: string, useServerCheck: boolean = false): boolean {
        let vm = this

        // Don't allow anything if the user isn't logged in...
        if (!sessionStorage.getItem('tdw.auth.authed')) {
            return false
        }
        //console.debug('Check Authorization for ' + checkPermission);
        if (useServerCheck) {
            // return false;
        }

        let perms: Array<string> = _.map(_.flatten(_.map(this.Session.userRoles, 'permissions')), 'name')

        // If one permission is true, this is allowed.
        let allow: boolean = _.some(perms, function (perm: string) {
            return vm.permissionService.implies(perm, checkPermission)
        })
        //console.debug('Check Authorization Allow ' + checkPermission + " -> " + allow);

        return allow
    }

    logout() {
        let vm = this

        console.debug('Logout called...')

        // Clear $http cache
        // $cacheFactory.get('$http').removeAll();

        // Clear session storage
        sessionStorage.clear()

        // Dispose any and all dialogs. This will still call the reject function for all promises attached
        // to open dialogs, but those shouldn't be able to cause much trouble at this point
        // $uibModalStack.dismissAll();

        // When the logout API request comes back, clear out any remaining local state
        let clearLocalState = function () {
            sessionStorage.setItem('tdw.auth.authed', '')
            vm.Session.destroy()
            vm.permissionService.clearCache()
        }

        // If a solution is still open, close it now, then let the server know we're logging out...
        let url = '/api/auth/logout'
        if (!vm.logoutPromise) {
            // Cache the promise so we don't kick off multiple logouts.
            vm.logoutPromise = vm.$http
                .get(url)
                .toPromise()
                .then(
                    function (response: any) {
                        console.debug('Log out success... status=' + response.status)
                        clearLocalState()
                        vm.logoutPromise = null
                    },
                    function (response: any) {
                        console.debug('Log out failed ... status=' + response.status)
                        clearLocalState()
                        vm.logoutPromise = null
                    }
                )
        }
        return vm.logoutPromise
    }

    login(username: string, password: string) {
        let vm = this
        console.debug('Login called... username=' + username)
        vm.Session.destroy()
        let url = '/api/auth/login'
        console.debug('URL: ' + url)
        return vm.$http
            .post(url, null, {
                headers: {
                    Authorization: 'Basic ' + btoa(username + ':' + password),
                    'No-Auth-Challenge': 'true',
                },
            })
            .toPromise()
            .then(
                function (response: any) {
                    //Load the Role information from the server...
                    //      Should be a list of roles with permissions attached.
                    vm.Session.create(response.session_id, response.subject_name, response.roles, null)
                    sessionStorage.setItem('tdw.auth.authed', 'true')
                    sessionStorage.setItem('tdw.auth.tabToken', uuid.v1())

                    vm.clikCRMService.getAccount().then(function (acct) {
                        vm.Session.crmAccounts = acct
                    })

                    console.debug('Log in success... data=' + JSON.stringify(response))
                    return response
                },
                function (response) {
                    console.debug('Log in failed ... data=' + JSON.stringify(response))
                    vm.Session.destroy()
                    return new Promise(function (accept, reject) {
                        reject(response)
                    })
                }
            )
    }

    // Logout redirect
    canActivate(): boolean {
        if (this.authenticated()) {
            return true
        }
        this.logout()
        return false
    }
}
